import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Pencil, AlertCircle } from 'lucide-react';
import { useRecipeDetail } from '../hooks/useRecipeDetail';
import { RecipeForm } from '../components/RecipeForm';
import api from '@/lib/api';

export function RecipeDetailPage() {
  const { recipeId } = useParams<{ recipeId: string }>();
  const navigate = useNavigate();
  const { recipe, loading, error, refresh } = useRecipeDetail(recipeId, true);
  const [editing, setEditing] = useState(false);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !recipe) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'Recipe not found'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <h1 className="text-2xl font-semibold">Edit Recipe</h1>
        <RecipeForm
          initialData={{
            ...recipe,
            description: recipe.description ?? undefined,
          }}
          onSubmit={async (data) => {
            await api.updateCraftingRecipe(recipeId!, { ...data });
            setEditing(false);
            refresh();
          }}
          onCancel={() => setEditing(false)}
          submitLabel="Update"
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/a/arche_crafting/admin')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">{recipe.name}</h1>
        <div className="flex gap-1 ml-2">
          <Badge variant="outline">{recipe.recipeType}</Badge>
          {!recipe.isEnabled && <Badge variant="destructive">Disabled</Badge>}
          {recipe.requiresLearned && <Badge variant="secondary">Learnable</Badge>}
        </div>
        <div className="ml-auto">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      {recipe.description && <p className="text-muted-foreground">{recipe.description}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Inputs</CardTitle>
          </CardHeader>
          <CardContent>
            {recipe.inputs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No inputs</p>
            ) : (
              <ul className="space-y-1">
                {recipe.inputs.map((inp, i) => (
                  <li key={i} className="text-sm">
                    {inp.quantity}x {inp.itemName}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Outputs</CardTitle>
          </CardHeader>
          <CardContent>
            {recipe.outputs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No outputs</p>
            ) : (
              <ul className="space-y-1">
                {recipe.outputs.map((out, i) => (
                  <li key={i} className="text-sm">
                    {out.quantity}x {out.itemName}
                    {out.probability != null && out.probability < 1 && (
                      <span className="text-muted-foreground ml-1">
                        ({Math.round(out.probability * 100)}%)
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <span className="font-medium">Level:</span> {recipe.level}
        </div>
        <div>
          <span className="font-medium">Created:</span>{' '}
          {new Date(recipe.createdAt).toLocaleDateString()}
        </div>
        <div>
          <span className="font-medium">Updated:</span>{' '}
          {new Date(recipe.updatedAt).toLocaleDateString()}
        </div>
      </div>

      {Object.keys(recipe.constraints).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Constraints</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-3 rounded overflow-auto">
              {JSON.stringify(recipe.constraints, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}

      {Object.keys(recipe.metadata).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Metadata</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-3 rounded overflow-auto">
              {JSON.stringify(recipe.metadata, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
